import { Button, Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import { Check, Xmark } from "iconoir-react"
import { useEffect, useState } from "react"
import {
  browserName,
  browserVersion,
  isChrome,
  isSafari,
  isSamsungBrowser,
  osName,
} from "react-device-detect"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import AllowCameraDialog from "../../../Assessments/Instructions/AllowCameraDialog"
import BrowserSpecificAsset from "../../../Assessments/Instructions/BrowserSpecificAsset"
import UnsupportedBrowserDialog from "../../../Assessments/Instructions/SystemRequirement/UnsupportedBrowserDialog"

interface SystemChecksProps {
  browserSupported: boolean
  cameraPermission: "pending" | "granted" | "denied"
  handleSystemCheckProceed: () => void
}

const SystemChecks = ({
  browserSupported,
  cameraPermission,
  handleSystemCheckProceed,
}: SystemChecksProps) => {
  const [showCameraHelp, setShowCameraHelp] = useState(false)
  const [showBrowserHelp, setShowBrowserHelp] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 2000)
    return () => clearTimeout(timer)
  }, [])

  const allChecksPassed = browserSupported && cameraPermission === "granted"

  const getBrowserIcon = () => {
    if (isSafari)
      return (
        <img
          src="/assets/logos/safari-logo-browser.png"
          alt="Safari"
          className="size-2.5"
        />
      )
    if (isChrome)
      return (
        <img
          src="/assets/logos/google-chrome-logo.png"
          alt="Chrome"
          className="size-2.5"
        />
      )
    if (isSamsungBrowser)
      return (
        <img
          src="/assets/logos/samsung-browser-logo.jpg"
          alt="Samsung"
          className="size-2.5"
        />
      )
    return null
  }

  return (
    <div className="relative">
      <AllowCameraDialog
        open={showCameraHelp}
        onOpenChange={setShowCameraHelp}
      />
      <UnsupportedBrowserDialog
        open={showBrowserHelp}
        onOpenChange={setShowBrowserHelp}
      />
      <Typography variant="title2" className="mb-4 text-onSurface-900 md:mb-6">
        {allChecksPassed
          ? "All Set. Please Proceed!"
          : "System Check & Permissions"}
      </Typography>

      <div className="space-y-4">
        {/* Step 1: System Compatibility */}
        <div>
          <div
            className={clsx(
              "flex items-center gap-2 rounded-xl border p-2 transition-all md:gap-4",
              browserSupported
                ? "border-success-200 bg-success-50/50"
                : "border-critical-200 bg-critical-50/50"
            )}
          >
            <div
              className={clsx(
                "flex size-5 items-center justify-center rounded-full",
                isLoading
                  ? "bg-onSurface-100 text-onSurface-400"
                  : browserSupported
                  ? "bg-success-500 text-white"
                  : "bg-critical-500 text-white"
              )}
            >
              {isLoading ? (
                <div className="size-2.5 animate-spin rounded-full border-2 border-onSurface-300 border-t-onSurface-700" />
              ) : browserSupported ? (
                <Check className="size-2.5" strokeWidth={2} />
              ) : (
                <Xmark className="size-2.5" strokeWidth={2} />
              )}
            </div>
            <div>
              <Typography variant="strong" className="block text-onSurface-900">
                System Compatibility
              </Typography>
              {!browserSupported && (
                <div className="flex flex-col items-start gap-1">
                  <Typography variant="smallBody" className="text-critical-600">
                    Unsupported Browser: {browserName}
                  </Typography>
                  <Button
                    variant="outlined"
                    size={"xs"}
                    className="mt-1 h-auto border-critical-600 text-critical-600"
                    onClick={() => {
                      trackingService.trackEvent(
                        TrackingEvents.cpatProcInitBrowserTroubleshootClicked,
                        {
                          browser_name: browserName,
                          browser_version: browserVersion,
                          os_name: osName,
                        },
                        { services: ["PostHog"] }
                      )
                      setShowBrowserHelp(true)
                    }}
                  >
                    Troubleshoot Issue
                  </Button>
                </div>
              )}
              {browserSupported && (
                <div className="mt-0.5 flex items-center gap-1.5 opacity-80">
                  {getBrowserIcon()}
                  <Typography variant="smallBody" className="text-success-700">
                    Browser Supported
                  </Typography>
                </div>
              )}
            </div>
          </div>
          <Typography
            variant="smallBody"
            className="text-onSurface-500"
            style={{ fontSize: 8 }}
          >
            Currently using: {browserName} {browserVersion} on {osName}
          </Typography>
        </div>

        {/* Step 2: Permissions */}
        <div
          className={clsx(
            "flex items-center gap-2 rounded-xl border p-2 transition-all md:gap-4",
            cameraPermission === "granted"
              ? "border-success-200 bg-success-50/50"
              : cameraPermission === "denied"
              ? "border-critical-200 bg-critical-50/50"
              : "border-onSurface-200 bg-white"
          )}
        >
          <div
            className={clsx(
              "flex size-5 items-center justify-center rounded-full transition-colors",
              isLoading
                ? "bg-onSurface-100 text-onSurface-400"
                : cameraPermission === "granted"
                ? "bg-success-500 text-white"
                : cameraPermission === "denied"
                ? "bg-critical-500 text-white"
                : "bg-onSurface-100 text-onSurface-400"
            )}
          >
            {isLoading ? (
              <div className="size-2.5 animate-spin rounded-full border-2 border-onSurface-300 border-t-onSurface-700" />
            ) : cameraPermission === "granted" ? (
              <Check className="size-2.5" strokeWidth={2} />
            ) : cameraPermission === "denied" ? (
              <Xmark className="size-2.5" strokeWidth={2} />
            ) : (
              <div className="size-2 rounded-full bg-current" />
            )}
          </div>
          <div className="flex-1">
            <Typography variant="strong" className="block text-onSurface-900">
              Webcam Permissions
            </Typography>
            {cameraPermission === "granted" ? (
              <Typography
                variant="smallBody"
                className="mt-0.5 text-success-700"
              >
                Permissions Granted
              </Typography>
            ) : cameraPermission === "denied" ? (
              <Button
                variant="outlined"
                size={"xs"}
                className="mt-1 h-auto border-critical-600 text-critical-600"
                onClick={() => {
                  trackingService.trackEvent(
                    TrackingEvents.cpatProcInitCameraTroubleshootClicked,
                    {
                      camera_permission: cameraPermission,
                    },
                    { services: ["PostHog"] }
                  )
                  setShowCameraHelp(true)
                }}
              >
                Troubleshoot Issue
              </Button>
            ) : (
              <div className="mt-2 rounded-xl border border-primary-100 bg-primary-50 p-2 text-primary-800 lg:p-3">
                <Typography>
                  Please click &apos;Allow&apos; if you get prompted for camera
                  permissions
                </Typography>

                <div className="mt-1">
                  <BrowserSpecificAsset
                    data={{
                      MobileSafari: {
                        IOS: "/assets/camera-permission-steps/ios-safari-prompt.png",
                      },
                      Safari: {
                        MAC_OS:
                          "/assets/camera-permission-steps/macos-safari-prompt.jpeg",
                      },
                      Firefox: {
                        Windows:
                          "/assets/camera-permission-steps/firefox-settings-prompt.png",
                        MAC_OS:
                          "/assets/camera-permission-steps/firefox-settings-prompt.png",
                      },
                      Chrome: {
                        IOS: "/assets/camera-permission-steps/ios-chrome-prompt.png",
                        Android:
                          "/assets/camera-permission-steps/android-chrome-prompt.png",
                        Windows:
                          "/assets/camera-permission-steps/windows-chrome-prompt.png",
                        MAC_OS:
                          "/assets/camera-permission-steps/macos-chrome-prompt.jpeg",
                      },
                      SamsungBrowser: {
                        Android:
                          "/assets/camera-permission-steps/samsung-browser-prompt.png",
                      },
                    }}
                    render={src => (
                      <img
                        src={src}
                        alt="Camera Permission Guide"
                        className="size-full object-contain"
                      />
                    )}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instruction Box */}
      <div className="my-3 flex gap-3 rounded-lg border-l-4 border-black bg-surface-100 p-2 md:my-8 md:p-4">
        <Typography
          variant="smallBody"
          className="leading-relaxed text-onSurface-700"
        >
          Permissions are required as this is a Proctored Test. Please note that
          you will be monitored via video/screen feed during the conduct of this
          session.
        </Typography>
      </div>

      <Button
        variant="filled"
        color="black"
        size="lg"
        className="w-full"
        disabled={
          isLoading || !browserSupported || cameraPermission !== "granted"
        }
        onClick={() => {
          trackingService.trackEvent(
            TrackingEvents.cpatProcInitSystemCheckProceedClicked,
            {
              browser_name: browserName,
              browser_version: browserVersion,
              os_name: osName,
              browser_supported: browserSupported,
              camera_permission: cameraPermission,
            },
            { services: ["PostHog"] }
          )
          handleSystemCheckProceed()
        }}
      >
        Proceed
      </Button>
    </div>
  )
}

export default SystemChecks
